import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useIntl } from "react-intl";
import {
  GetQualificationsRequest,
  IQualification,
  ListResponseOfQualification,
  QualificationFilter,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import { IdValue } from "types/common-types";
import { formatQualification } from "helpers/FormatHelpers";

type SelectQualificationProps = {
  organizationId: string;
  useBaseTable?: boolean;
  value?: string;
  onChange: (qualification: IQualification | null) => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<IdValue>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectQualification = ({
  onChange,
  value,
  organizationId,
  useBaseTable,
  ...rest
}: SelectQualificationProps) => {
  const { formatMessage: t } = useIntl();
  const { qualificationService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [selectedDisplay, setSelectedDisplay] = useState<IdValue | null>(null);
  const [qualifications, setQualifications] = useState<IQualification[]>([]);

  const initialRequest = new GetQualificationsRequest({
    organizationId,
    sortField: "value",
    parameters: QualificationFilter.fromJS({ useBaseTable }),
  });

  const { isLoading, data } = useQuery<ListResponseOfQualification>(
    ["qualificationService.getQualifications", initialRequest],
    () => qualificationService.getQualifications(initialRequest),
    { enabled: !!organizationId },
  );

  useEffect(() => {
    if (!isLoading && data?.data && value) {
      const match = data.data.find((d) => d.id === value);
      if (match) {
        setQualifications(data.data);
        setSelectedDisplay({
          id: match.id,
          value: formatQualification(match),
        });
      }
    }
  }, [isLoading, data, value]);

  const fetchQualifications = useCallback(
    async (searchTerm: string) => {
      const searchRequest = new GetQualificationsRequest({
        organizationId,
        pageNumber: 0,
        pageSize: 20,
        sortField: "value",
        parameters: QualificationFilter.fromJS({
          useBaseTable,
          searchText: searchTerm === "" ? undefined : searchTerm,
        }),
      });

      const result = await queryClient.fetchQuery<ListResponseOfQualification>(
        ["qualificationService.getQualifications", searchRequest],
        () => qualificationService.getQualifications(searchRequest),
      );

      setQualifications(result.data ?? []);

      return {
        result: {
          ...result,
          data: (result.data ?? []).map((q) => ({
            id: q.id,
            value: formatQualification(q),
          })),
        },
        forSearchTerm: searchTerm,
      };
    },
    [organizationId, qualificationService, queryClient, useBaseTable],
  );

  const handleChange = (item: IdValue | null) => {
    setSelectedDisplay(item);
    onChange(qualifications.find((q) => q.id === item?.id) ?? null);
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={selectedDisplay}
      isLoadingInitialValue={isLoading}
      labelField="value"
      idField="id"
      fetchData={fetchQualifications}
      failMessage={t({ id: "selectAccreditationBodyFetchFailed" })}
      onChange={handleChange}
    />
  );
};

export default SelectQualification;
